import { FC, useCallback } from 'react'
import {
  AddressAutocompleteField,
  type StructuredAddress,
} from 'src/components/shared/address-autocomplete-field'
import type { AddressValue } from 'src/services/survey/models'

interface AddressFieldProps {
  value?: AddressValue | string | null
  disabled?: boolean
  handleResponse: (value: AddressValue | null) => void
  // Props below exist for interface consistency with other field types
  variant?: string
  handleSearch?: (value: string) => void
  options?: unknown[]
  loading?: boolean
  isMultiple?: boolean
  numPad?: boolean
  questionDisplay?: string | null
  layout?: string | null
  valueRef?: unknown
  validatorsRef?: Array<{ validationType: string; value: Record<string, unknown> }>
  patternError?: string | null
  onPatternError?: (error: string | null) => void
  questionId?: string
}

/** Convert StructuredAddress → AddressValue (adds required rawAddress field). */
function toAddressValue(sa: StructuredAddress): AddressValue {
  return {
    rawAddress: sa.rawAddress ?? '',
    streetAddress: sa.streetAddress,
    city: sa.city,
    state: sa.state,
    postalCode: sa.postalCode,
    country: sa.country,
    latitude: sa.latitude ?? null,
    longitude: sa.longitude ?? null,
    placeId: sa.placeId ?? null,
  }
}

/** Convert AddressValue → StructuredAddress. */
function toStructuredAddress(av: AddressValue): StructuredAddress {
  return {
    rawAddress: av.rawAddress,
    streetAddress: av.streetAddress,
    city: av.city,
    state: av.state,
    postalCode: av.postalCode,
    country: av.country,
    latitude: av.latitude,
    longitude: av.longitude,
    placeId: av.placeId,
  }
}

const AddressField: FC<AddressFieldProps> = ({ handleResponse, value, disabled = false }) => {
  const parsedValue: StructuredAddress | null =
    value && typeof value === 'object' ? toStructuredAddress(value) : null

  const handleChange = useCallback(
    (sa: StructuredAddress | null) => {
      handleResponse(sa ? toAddressValue(sa) : null)
    },
    [handleResponse],
  )

  return (
    <AddressAutocompleteField value={parsedValue} onChange={handleChange} disabled={disabled} />
  )
}

export default AddressField
